import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSetUpMap } from "../../../../App/useSetUpMap";
import { useNotificationsLayer } from '../../../../App/useNotificationsLayer';
import MapContainer from '../../../../components/Maps/MapContainer';
import { MapContext } from '../../../../App/MapContextContainer';
import { PREFERRED_WKID } from '../../../CustomAreas/UserDataContextContainer';
import { NaturalForestsContext } from '../../NaturalForestsContextContainer';
import useOpenAreasLayer from '../../../../components/Maps/useOpenAreasLayer';
import { AppSettingsContext } from '../../../../App/AppSettingsContextContainer';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import useFeatureLayer from './useFeatureLayer';
import { getFromAndToDatesFromMonths } from '../../../utils';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { useTranslation } from 'react-i18next';
import useSetPopupContent from './useSetPopupContent';
import PopupTemplate from '@arcgis/core/PopupTemplate';
import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';

export default function NaturalForestsMap() {
    const elementRef = useRef(null);
    const { webmap, view } = useSetUpMap(elementRef, "6e34c9c7b08d41779b23bf1241649275");
    const mapContext = useContext(MapContext);
    useNotificationsLayer(webmap, view, new Date(2019, 0, 1));
    const [isLoadingForestPoints, setIsLoadingForestPoints] = useState(false);
    const naturalForestsContext = useContext(NaturalForestsContext);
    const { appliedFilters } = useContext(AppSettingsContext);
    const { t, i18n } = useTranslation("naturalForests");
    const setPopupContent = useSetPopupContent();

    const openAreasLayer = useOpenAreasLayer(view);

    const naturalForestPointsLayer = useFeatureLayer();

    const naturalForestsLayer = useMemo(() =>
        new FeatureLayer({
            url: "https://services6.arcgis.com/f3AgXyz2GHmCyuKI/arcgis/rest/services/55_luonnonmetsaa_1/FeatureServer/0",
            minScale: 100000,
            renderer: new SimpleRenderer({
                symbol: new SimpleFillSymbol({
                    color: "#74b566",
                    outline: {
                        color: "black",
                        width: 1
                    }
                }),
            }),
        }), []);

    useEffect(() => {
        if (naturalForestsLayer) {
            naturalForestsLayer.title = t("luonnonmetsäKerroksenOtsikko");
        }
    }, [i18n.language, t]);

    useEffect(() => {
        if (mapContext.mapAndViewReady === false) {
            return;
        }

        const popup = new PopupTemplate({
            title: t("luonnonmetsäKerroksenPopupOtsikko"),
            outFields: ["gp_link", "kunta", "maakunta", "nimi", "ala_ha"],
            content: setPopupContent
        });

        naturalForestsLayer.popupTemplate = popup;
    }, [i18n.language, t, mapContext.mapAndViewReady]);

    useEffect(() => {
        const asyncCall = async () => {
            if (mapContext.mapAndViewReady && naturalForestsContext.naturalForestsWithClashData) {
                setIsLoadingForestPoints(true);

                const geojson = naturalForestsContext.naturalForestsWithClashData;

                const bufferedGraphics: Graphic[] = [];

                for (const feature of geojson.features) {
                    const g = new Graphic({
                        attributes: feature.properties,
                    });

                    const p: Point = new Point({
                        latitude: feature.geometry.coordinates[1],
                        longitude: feature.geometry.coordinates[0],
                        spatialReference: { wkid: PREFERRED_WKID },
                        hasZ: false
                    });

                    g.geometry = p;

                    bufferedGraphics.push(g);
                }

                const addEdits = {
                    addFeatures: bufferedGraphics
                };

                await naturalForestPointsLayer.applyEdits(addEdits);

                webmap.add(naturalForestsLayer);
                webmap.add(naturalForestPointsLayer);
                webmap.add(openAreasLayer);

                setIsLoadingForestPoints(false)
            }
        }

        asyncCall();
    }, [mapContext.mapAndViewReady, naturalForestsContext.naturalForestsWithClashData, webmap]);

    useEffect(() => {
        const asyncCall = async () => {
            const query = naturalForestPointsLayer.createQuery();
            const features = await naturalForestPointsLayer.queryFeatures(query);

            if (appliedFilters?.declarationArrivalMonths) {
                const { fromDate } = getFromAndToDatesFromMonths(appliedFilters.declarationArrivalMonths);

                if (fromDate) {
                    const firstDayOfMonth = new Date(`${fromDate.getFullYear()}-${(fromDate.getMonth() + 1 + "").padStart(2, "0")}-01T00:00:00Z`);

                    features.features.forEach(feature => {
                        const date = new Date(feature.attributes["latestClash"]);

                        if (feature.attributes.status === "3") {
                            // No-op, we don't change these ever
                        }
                        else if (date < firstDayOfMonth) {
                            feature.attributes.status = "1";
                        }
                        else {
                            feature.attributes.status = "2";
                        }
                    });

                    const edits = {
                        updateFeatures: features.features
                    };

                    await naturalForestPointsLayer.applyEdits(edits);
                }
            }
            else {
                features.features.forEach(feature => {
                    const date = feature.attributes["latestClash"] !== null ? new Date(feature.attributes["latestClash"]) : null;

                    if (feature.attributes.status === "3") {
                        // No-op, we don't change these ever
                    }
                    else if (date) {
                        feature.attributes.status = "2";
                    }
                    else {
                        feature.attributes.status = "1";
                    }
                });

                const edits = {
                    updateFeatures: features.features
                };

                await naturalForestPointsLayer.applyEdits(edits);
            }
        }

        asyncCall();
    }, [appliedFilters, isLoadingForestPoints]);

    return <MapContainer customVisibilityCheckFn={() => isLoadingForestPoints} elementRef={elementRef} view={view}></MapContainer>
}
